import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type {
  ClaimableQuotationSendChannel,
  QuotationConvertPreview,
} from "@steelbill/types";
import { api, requestBlob } from "@/lib/api";
import {
  endpoints,
  type DocumentEnvelope,
  type InvoiceDetail,
  type QuotationDetail,
  type QuotationEmailResult,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/** `GET /quotations/:id` → the quotation, lines and all. */
export function useQuotation(id: string | undefined): UseQueryResult<QuotationDetail> {
  return useQuery({
    queryKey: queryKeys.quotations.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<QuotationDetail>>(
        endpoints.quotation(id ?? ""),
      );
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    /*
     * SHORTER THAN AN INVOICE'S FIVE MINUTES, AND DELIBERATELY SO. An issued
     * invoice is immutable, so its cache can sit for as long as it likes. A
     * quotation is EDITABLE while DRAFT or SENT (§7), and it also moves under
     * this screen's feet in ways the screen did not cause: a conversion raises
     * an invoice and flips the status to CONVERTED or PARTIALLY_CONVERTED.
     *
     * Thirty seconds keeps re-opening a quotation from the register cheap while
     * making a stale "Convert" button — the one that would try to invoice lines
     * already invoiced — a thirty-second window rather than a five-minute one.
     * The server rejects that attempt regardless; this is about not offering it.
     */
    staleTime: 30_000,
  });
}

/**
 * The PDF — fetched as BYTES, never navigated to.
 *
 * Same reasoning as `useInvoicePdf`, and for the same two reasons: the route is
 * authenticated with a BEARER token, so a browser navigation (an `<a href>` or
 * an `<iframe src>`) sends no Authorization header and renders the API's 401
 * JSON inside a PDF viewer; and every object URL is revoked, because
 * `createObjectURL` pins the whole blob in memory until it is.
 *
 * NO `copy` PARAMETER, UNLIKE AN INVOICE. §9.2's ORIGINAL / DUPLICATE /
 * TRIPLICATE captions are a tax-invoice requirement — the transporter's copy
 * travels with the goods. A quotation is not a tax document and has no statutory
 * copies, so offering the choice here would invent a rule that does not exist.
 */
export function useQuotationPdf(id: string | undefined, number: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const release = () => {
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => release, []);

  const mutation = useMutation({
    mutationFn: async () => {
      const fallback = `${(number ?? `quotation-${id ?? ""}`).replace(/\//g, "-")}.pdf`;
      return requestBlob(endpoints.quotationPdf(id ?? ""), { fallbackFilename: fallback });
    },
    onSuccess: (result) => {
      release();
      const next = URL.createObjectURL(result.blob);
      urlRef.current = next;
      setUrl(next);
      setFilename(result.filename);
    },
  });

  return { ...mutation, url, filename, release };
}

/**
 * `POST /quotations/:id/void { reason }`.
 *
 * VOID, NOT CANCEL, AND NOT DELETE. The wording differs from an invoice's
 * because the act differs: cancelling a tax invoice has a statutory meaning and
 * a credit note behind it (§17.3), whereas withdrawing an offer has none. What
 * is identical is that the row survives with its number — the quotation series
 * is gap-free too (§6), and a missing 542/26 reads as a lost document rather
 * than a withdrawn offer.
 *
 * TERMINAL. There is no un-void, and the server refuses a second one, so the
 * caller must be sure before it fires. Invalidate the detail AND the register
 * afterwards; the list shows status.
 */
export function useVoidQuotation(id: string | undefined) {
  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post<DocumentEnvelope<QuotationDetail>>(
        endpoints.quotationVoid(id ?? ""),
        { reason },
      );
      return response.data;
    },
  });
}

/**
 * `POST /quotations/:id/status { status, reason? }` — the MANUAL half of the
 * lifecycle (§7).
 *
 * Only the statuses a human decides are accepted here: SENT when the offer goes
 * out, APPROVED or REJECTED when the customer answers, EXPIRED when it lapses.
 *
 * CONVERTED and PARTIALLY_CONVERTED ARE NOT IN THIS LIST and the server rejects
 * them by name. They are FACTS ABOUT AN INVOICE EXISTING, not opinions about the
 * offer — set only by `POST /:id/convert`, which raises the bill in the same
 * transaction. Letting a screen assert "converted" without a bill behind it
 * would put a quotation in a state its own register cannot justify. VOID is
 * likewise excluded: it needs a reason, so it has its own route above.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * `channel` — HOW THE OFFER WENT OUT, AND WHY IT IS ON THIS HOOK.
 *
 * There used to be three controls that could mark a quotation sent: this one,
 * and two in the approval card that posted to `POST /:id/record-sent`. The owner
 * asked for ONE ("we already have mark as sent, so remove sent on whatsapp and
 * record as sent"), and they were not the same act — this route moved the STATUS
 * and left `sent_at` and `sent_channel` NULL, while the other stamped both. So
 * the channel came here rather than the control going there, and the server now
 * stamps the columns from this request.
 *
 * WHAT SENDS WHAT: the Share → WhatsApp flow passes `WHATSAPP` because that
 * share genuinely happened; the plain "Mark as sent" button passes nothing and
 * the server reads that as `RECORDED` — a person asserting the customer has it,
 * which is exactly what that button claims. `EMAIL` and `LINK` are refused by
 * the schema: only the routes that OBSERVE those may write them.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useSetQuotationStatus(id: string | undefined) {
  return useMutation({
    mutationFn: async (input: {
      status: string
      reason?: string
      channel?: ClaimableQuotationSendChannel
    }) => {
      const response = await api.post<DocumentEnvelope<QuotationDetail>>(
        endpoints.quotationStatus(id ?? ""),
        input,
      );
      return response.data;
    },
  });
}

/**
 * `POST /quotations/:id/email` — send this quotation to its customer (§17A.2).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT SENDS NOTHING FROM HERE, AND THAT IS THE WHOLE SHAPE OF IT. The request has
 * no body: no recipient, no subject, no message. The API reads the customer's
 * own address, renders the quotation's PDF server-side and attaches it. There is
 * nothing for this client to get wrong, and nothing it could be talked into
 * sending somewhere else.
 *
 * THE ANSWER MOVES THE DOCUMENT, so the caller must invalidate. A DRAFT becomes
 * SENT — sending IS the moment the app knows the offer went out — and
 * `statusChanged` says whether that happened, so the toast can be honest about
 * it rather than claiming a transition every time.
 *
 * NO `onSuccess` HERE. The invalidation belongs to the screen, which is where
 * `queryClient` already lives and where the WhatsApp path does the same thing.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useEmailQuotation(id: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<DocumentEnvelope<QuotationEmailResult>>(
        endpoints.quotationEmail(id ?? ""),
        // NO BODY. The strict schema on the server refuses any field, so there
        // is nothing to send but the id already in the path.
        {},
      );
      return response.data;
    },
  });
}

/** What `POST /quotations/:id/convert` accepts (§7.1). */
export interface QuotationConvertInput {
  /** `YYYY-MM-DD`. The INVOICE's date, which decides its rates (§8.7) and FY. */
  date?: string;
  soNumber?: string;
  soDate?: string;
  /**
   * PARTIAL CONVERSION — the ids of the quotation lines to bill now. Omitted
   * means "all of them", which is the ordinary case. Sending a subset leaves the
   * quotation PARTIALLY_CONVERTED and the remaining lines still billable, which
   * is how a staged job gets invoiced in instalments against one offer.
   */
  lineIds?: number[];
}

/** What `POST /quotations/:id/convert` answers with — BOTH halves of it. */
export interface ConvertQuotationResult {
  /** The bill that now exists, with the number it took from the series (§6). */
  invoice: InvoiceDetail;
  /**
   * THE SERVER'S OWN SENTENCES ABOUT THE DOCUMENT IT JUST WROTE, and they are
   * not the preview's. The commit re-runs the plan under a row lock against the
   * bill's real date, so this is the list that describes the invoice that
   * EXISTS: §7.1's "Tax rate changed since this quotation. New total: ₹X (was
   * ₹Y)." sentence, `draftBecauseNoSalesOrder`, and the customer-drift
   * warnings. Never empty by construction — a caller that ignores it is a
   * caller that converts silently, which §7.1 forbids.
   */
  warnings: string[];
}

/**
 * `POST /quotations/:id/convert` → the INVOICE it raised (§7.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS ALLOCATES AN INVOICE NUMBER, so it is not a navigation and not a preview:
 * it is the write that turns an offer into a tax document, and it consumes a
 * number from the gap-free series (§6) inside the same transaction that links
 * the two rows. Firing it twice raises two bills.
 *
 * The answer is an `InvoiceDetail`, NOT a quotation — the caller navigates to
 * the new bill, and must invalidate the quotation detail (its status moved),
 * both registers, and the dashboard (a bill now counts toward the month).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT RETURNS `warnings` TOO, AND THAT IS THE POINT OF THE SHAPE. This used to
 * be `return response.data`, which threw the array away inside the hook where no
 * caller could have recovered it — so §7.1's mandatory rate-change sentence was
 * emitted by the server, serialised onto the wire, and dropped one line before
 * anything could render it.
 *
 * The preview panel is NOT a substitute. It only ever describes a conversion
 * that has not happened, it is skipped entirely when the dry run fails and the
 * owner takes "Make the bill without checking", and the standing "today's GST
 * rates" bullet that used to cover the gap was deliberately deleted. On that
 * path the commit's warnings are the ONLY notice a rate moved.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useConvertQuotation(id: string | undefined) {
  return useMutation({
    mutationFn: async (input: QuotationConvertInput): Promise<ConvertQuotationResult> => {
      const response = await api.post<DocumentEnvelope<InvoiceDetail> & { warnings?: string[] }>(
        endpoints.quotationConvert(id ?? ""),
        input,
      );
      return { invoice: response.data, warnings: response.warnings ?? [] };
    },
  });
}

/** `POST /quotations/:id/convert/preview` → `{ data, warnings }`. */
export interface ConvertPreviewResult {
  preview: QuotationConvertPreview;
  /** The server's own list, in the order the conversion emits it. Advice. */
  warnings: string[];
}

/**
 * THE DRY RUN — what the conversion would produce, before it spends a number.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THE CONFIRM DIALOG WAITS FOR THIS. `POST /:id/convert` allocates an
 * invoice number and §6 never releases one: a cancelled invoice keeps its
 * number, because a gap in the series is a question from the department. So the
 * dialog used to ask the owner to accept a document he had been shown no figure
 * for, under a standing sentence about "today's GST rates" printed on every
 * conversion whether or not a rate had moved — true, unactionable, and therefore
 * the warning nobody reads.
 *
 * This answers with figures instead: the totals, the supply type, whether the
 * bill lands as a DRAFT, and per line the old rate, the new rate and what the
 * move costs in paise. It writes nothing at all.
 *
 * A QUERY, NOT A MUTATION, EVEN THOUGH IT IS A POST. It is a read that happens
 * to need a body. Making it a query is what gets it fetched once when the dialog
 * opens, cached against its inputs, and re-run when those inputs change —
 * rather than fired by hand from a render.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `soNumber` IS PART OF THE REQUEST AND OF THE KEY, because `wouldBeStatus`
 * depends on it: the same lines convert to an ISSUED bill with the customer's
 * S.O. number and to a DRAFT without it (§7.1). The SERVER decides which — the
 * predicate lives in `conversionInvoiceStatus` beside the commit that uses it,
 * so screen and server cannot disagree about what counts as "blank". Pass the
 * value the owner has typed, DEBOUNCED, and read `wouldBeStatus` back; never
 * re-derive the answer here.
 *
 * `retry: false`, deliberately. A 409 from this route is INFORMATION — "this
 * quotation is already converted in full", "line 3 is already on bill
 * 26-27/64" — and retrying an answer is pointless. A 404 means a feature flag
 * is off (§18.2), which is equally settled.
 *
 * `staleTime: 0`, also deliberately. Rates resolve by document date (§8.7) and
 * another bill may have been raised from these lines since the dialog last
 * opened, so a preview is only good for the moment it was taken.
 */
export function useConvertPreview(
  id: string | undefined,
  input: QuotationConvertInput,
  enabled: boolean,
): UseQueryResult<ConvertPreviewResult> {
  const soNumber = input.soNumber ?? "";
  const soDate = input.soDate ?? "";
  const lineIds = input.lineIds ?? [];

  return useQuery({
    /*
     * UNDER THE QUOTATION'S OWN DETAIL PREFIX, so `invalidateQueries` after a
     * conversion drops the preview along with the document it described. A key
     * of its own would leave a dry run for a quotation that has since become a
     * bill sitting in the cache, ready to be shown again.
     */
    queryKey: [...queryKeys.quotations.detail(id ?? ""), "convert-preview", soNumber, soDate, lineIds],
    queryFn: async () => {
      const response = await api.post<{ data: QuotationConvertPreview; warnings?: string[] }>(
        endpoints.quotationConvertPreview(id ?? ""),
        {
          /*
           * SENT EVEN WHEN BLANK. `soNumber: ""` is valid on
           * `QuotationConvertSchema` and is exactly how the commit hears "no
           * S.O. number yet" — so the dry run is asked the same question the
           * conversion will be asked, and answers `wouldBeStatus: "DRAFT"`
           * itself.
           */
          soNumber,
          /*
           * The caller sends a date only alongside its number — §9.2 prints the
           * pair or neither, and `QuotationConvertSchema` refuses the date
           * alone. That pairing is decided once, in the dialog, from the same
           * flag that disables the date field; this only forwards it.
           */
          ...(soDate === "" ? {} : { soDate }),
          ...(lineIds.length === 0 ? {} : { lineIds }),
        },
      );
      return { preview: response.data, warnings: response.warnings ?? [] };
    },
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    staleTime: 0,
    /*
     * KEEP THE LAST ANSWER ON SCREEN WHILE A NEW ONE IS FETCHED. Typing the
     * S.O. number changes the key, and without this the totals would blank to a
     * skeleton on each keystroke pause — figures flickering in and out of a
     * confirmation dialog is how someone stops reading them. The confirm button
     * is still disabled while the new answer is in flight; see ConvertDialog.
     */
    placeholderData: (previous) => previous,
  });
}
